import React from "react";
/**
 * Useful when wanting to show which items out of a list are in-flight.  For example, lets say you have a list of items
 * which a user can pick to take some action on (which takes some time), and you want to show which items are being processed.
 * You would use this hook in the following way:
 *
 * ```js
 * const [itemsBeingProcessed, takeAction] = useLoadingList(SomeService.process)
 * ...
 * <Item
 *  onClick = { () => takeAction([item.id], item) }
 *  isInProgress = {itemsBeingProcessed.includes(item.id)}
 * />
 * ```
 *
 * @param {function(...[P]): Promise<R>} serviceMethod - the function to process an item.
 * @returns {[T[], Function]} a list of items being processed and a method to process an item.
 * The function returned can be treated like the original service method with one exception: it now accepts
 * an additional argument as the first argument, which is the item to be stored in the loading list.
 *
 * T = type of the item in the processing list
 * P = type of args passed into service method
 * R = type of data resolved by promise returned by service method
 */
export default function useLoadingList(serviceMethod) {
    const [list, setList] = React.useState([]);
    const [errorList, setErrorList] = React.useState([]);
    // Memoize this function for reference stability
    const process = React.useMemo(() => 
    /**
     * Adds an item to the processing list and then removes it when processing is done.
     *
     * @param items - the items to process
     * @param args - other args passed to the service method
     */
    (items, ...args) => {
        // add the item ot the temp list
        // @ts-expect-error - TS2345 - Argument of type '(l: never[]) => T[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
        setList((l) => [...l, ...items]);
        // Call the service method with the args besides the item
        return (serviceMethod(...args)
            // We want this method to have the same response as the original, so return a response from .then so it will
            // be passed into the .then handler, and throw anything caught so that it will be caught by .catch.
            // In both cases, remove this item from the temporary list.
            .then((resp) => {
            setList((l) => l.filter((i) => !items.includes(i)));
            setErrorList((l) => l.filter((i) => !items.includes(i)));
            return resp;
        })
            .catch((err) => {
            setList((l) => l.filter((i) => !items.includes(i)));
            // @ts-expect-error - TS2345 - Argument of type '(l: never[]) => T[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
            setErrorList((l) => [...l, ...items]);
            throw err;
        }));
    }, [serviceMethod]);
    return [list, process, errorList];
}
